import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FittingTagRow, addFittingTag, fetchTagVocabulary, removeFittingTag } from '../api/fittings';
import { fetchSiteNames } from '../api/sites';
import { useAuth } from '../services/AuthContext';
import { C, FONT, hexToRgba, lcarsLabelSx } from '../theme/tokens';

/**
 * Tagging, for whoever is actually flying the thing.
 *
 * This used to be admin-only, on a separate Admin > Fittings screen: the one
 * action that gives a fit reach — "this is for C5s / for gas / for PvP" —
 * was a request you filed with an admin, so most fits stayed untagged and
 * the site pages that read those tags stayed empty. The knowledge lives with
 * the people flying the doctrine, so the control does too.
 *
 * A tag from anyone but the fitting's owner or an admin lands PENDING —
 * shown here greyed out, with how many votes it still needs — until a second
 * distinct member endorses the exact same tag (adding it again, the normal
 * way, is how you endorse one). The owner or an admin adding it is trusted
 * on the spot. Removal: an approved tag only comes off for the owner or an
 * admin; a still-pending one can also be withdrawn by whoever proposed it.
 * All of this is enforced server-side — this file only has to render it
 * consistently with what the server will actually accept.
 */
/** Mirrors the server's TAG_APPROVAL_VOTES (routes/fittings.ts) — purely for
 * the "needs N more" message; the vote itself is counted server-side. */
const TAG_APPROVAL_VOTES = 2;
/** The "level" a tag carries — a properly-cased label plus the one-line
 * explanation of what it actually means, shown as the field's helper text
 * rather than folded into a paragraph elsewhere. "Minimum" is the word
 * people misread as a preference ("I'd rather bring more") when it is
 * actually a claim about survival ("this is the floor, not the choice"),
 * so its hint says exactly that at the point where someone is picking it. */
const RECOMMENDATIONS: { value: 'recommended' | 'minimum'; label: string; hint: string }[] = [
  { value: 'recommended', label: 'Recommended', hint: 'What to bring — the fit this page should suggest.' },
  { value: 'minimum', label: 'Minimum', hint: 'The floor that survives here, not a preference for something smaller.' },
];

/** Offered when a corp has not built its own vocabulary yet — the categories
 * a wormhole corp actually sorts fits by. Free text is still accepted; these
 * only save the first person from a blank field.
 *
 * `wh_class` and `site` deliberately have no seeds here: those two are the
 * tags the app READS BACK (a site page asks for fits tagged with its own
 * name), so their values come from the real catalogue over the wire — a
 * hand-typed "Forgotten Frontier Quarantine Outpost" that is one word off
 * is a tag that silently reaches no page at all. */
const SEED_TYPES = ['wh_class', 'site', 'activity', 'doctrine'];

/** The two rows the import form offers as one-click tags, exported so the
 * form and this editor spell them identically — two lists would give a corp
 * "pvp" and "PVP" as separate filter chips within a week. */
export const WH_CLASS_TAGS = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'];
export const ACTIVITY_TAGS = [
  'pve',
  'pvp',
  'ratting',
  'mining',
  'gas huffing',
  'hauling',
  'scouting',
  'rolling',
];

const SEED_VALUES: Record<string, string[]> = {
  activity: ACTIVITY_TAGS,
  doctrine: ['home defense', 'roam', 'evict', 'budget'],
};

const FittingTagEditor: React.FC<{ fittingId: number; tags: FittingTagRow[]; isFittingOwner: boolean }> = ({
  fittingId,
  tags,
  isFittingOwner,
}) => {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [tagType, setTagType] = useState('activity');
  const [tagValue, setTagValue] = useState('');
  const [recommendation, setRecommendation] = useState<'minimum' | 'recommended'>('recommended');

  const { data: vocabulary } = useQuery({ queryKey: ['tag-vocabulary'], queryFn: fetchTagVocabulary });
  // Static reference data for the life of the app — fetched once and kept,
  // so opening the editor on a tenth fit doesn't re-ask for the same list.
  const { data: siteNames } = useQuery({ queryKey: ['site-names'], queryFn: fetchSiteNames, staleTime: Infinity });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['fitting', fittingId] });
    queryClient.invalidateQueries({ queryKey: ['fittings'] });
    queryClient.invalidateQueries({ queryKey: ['tag-vocabulary'] });
    // The site pages read these tags — a fit tagged for a site should show
    // up there without a reload.
    queryClient.invalidateQueries({ queryKey: ['fittings-by-site'] });
    queryClient.invalidateQueries({ queryKey: ['fittings-by-class'] });
    queryClient.invalidateQueries({ queryKey: ['fitting-recommendations'] });
  };

  const add = useMutation({
    mutationFn: (pick?: { type: string; value: string }) => addFittingTag(fittingId, pick?.type ?? tagType.trim(), pick?.value ?? tagValue.trim(), recommendation),
    onSuccess: () => {
      setTagValue('');
      invalidate();
    },
  });
  const remove = useMutation({ mutationFn: (tagId: number) => removeFittingTag(fittingId, tagId), onSuccess: invalidate });

  // Corp vocabulary first (what people here actually use), seeds behind it.
  const typeOptions = useMemo(
    () => [...new Set([...(vocabulary ?? []).map((v) => v.tag_type), ...SEED_TYPES])],
    [vocabulary],
  );
  const valueOptions = useMemo(() => {
    const used = (vocabulary ?? []).filter((v) => v.tag_type === tagType).map((v) => v.tag_value);
    const catalogue =
      tagType === 'site' ? siteNames?.sites : tagType === 'wh_class' ? siteNames?.whClasses : undefined;
    return [...new Set([...used, ...(catalogue ?? SEED_VALUES[tagType] ?? [])])];
  }, [vocabulary, siteNames, tagType]);

  // Matches the server exactly: approved is owner/admin only; pending is
  // also removable by whoever proposed it, retracting their own unconfirmed
  // suggestion.
  const canRemove = (tag: FittingTagRow) =>
    isFittingOwner || me.isAdmin || (!tag.approved && tag.created_by_character_id === me.character?.id);

  return (
    <Box>
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.62rem'), mb: 0.75 }}>tags</Typography>

      <Stack spacing={0.75} sx={{ mb: 1.5 }}>
        {[{ type: 'wh_class', label: 'WH class', values: WH_CLASS_TAGS },
          { type: 'activity', label: 'Activity', values: ACTIVITY_TAGS }].map((row) => (
          <Stack key={row.type} direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', width: 65 }}>{row.label}</Typography>
            {row.values.map((value) => {
              const tag = tags.find((t) => t.tag_type === row.type && t.tag_value === value && t.recommendation === recommendation);
              return <Chip key={value} label={value} size="small" variant={tag ? 'filled' : 'outlined'}
                disabled={add.isPending || remove.isPending || (!!tag?.approved && !canRemove(tag))}
                onClick={() => tag && canRemove(tag) ? remove.mutate(tag.id) : add.mutate({ type: row.type, value })} />;
            })}
          </Stack>
        ))}
      </Stack>

      {tags.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {tags.map((t) => {
            // Pending reads as a proposal, not a fact: dim border and text
            // regardless of recommendation colour, same "not settled yet"
            // treatment the rest of the app gives an unrated fitting.
            const accent = t.recommendation === 'minimum' ? C.gold : C.cyan;
            const needed = Math.max(0, TAG_APPROVAL_VOTES - t.endorsement_count);
            const tooltip = t.approved
              ? `${t.tag_type} · ${t.recommendation} — tagged by ${t.created_by_name ?? 'someone'}`
              : `${t.tag_type} · ${t.recommendation} — proposed by ${t.created_by_name ?? 'someone'}, pending: ${needed} more vote${needed === 1 ? '' : 's'} to confirm (add the same tag again to endorse it)`;
            return (
              <Stack
                key={t.id}
                direction="row"
                alignItems="center"
                spacing={0.25}
                sx={{
                  borderRadius: '999px',
                  border: `1px solid ${hexToRgba(accent, t.approved ? 0.4 : 0.2)}`,
                  opacity: t.approved ? 1 : 0.6,
                  pl: 1,
                  pr: canRemove(t) ? 0.25 : 1,
                  py: 0.15,
                }}
              >
                <Tooltip title={tooltip}>
                  <Stack direction="row" alignItems="center" spacing={0.4} sx={{ cursor: 'help' }}>
                    <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.7rem', color: t.approved ? accent : C.textDim }}>
                      {t.tag_value}
                    </Typography>
                    {!t.approved && (
                      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.6rem', color: C.textDim, fontStyle: 'italic' }}>
                        pending
                      </Typography>
                    )}
                  </Stack>
                </Tooltip>
                {canRemove(t) && (
                  <IconButton
                    size="small"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(t.id)}
                    sx={{ p: 0.15, color: C.textDim, '&:hover': { color: C.red } }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="flex-start">
        <Autocomplete
          size="small"
          freeSolo
          options={typeOptions}
          value={tagType}
          onInputChange={(_e, v) => setTagType(v)}
          sx={{ width: 150 }}
          renderInput={(params) => <TextField {...params} label="Category" />}
        />
        <Autocomplete
          size="small"
          freeSolo
          options={valueOptions}
          value={tagValue}
          onInputChange={(_e, v) => setTagValue(v)}
          sx={{ width: 190 }}
          renderInput={(params) => <TextField {...params} label="Tag" placeholder="C5, ratting, gas huffing…" />}
        />
        {/* A real select, not a native `<select>` — the native element used
            to render its own unstyled browser dropdown mid-row, the one
            control on this page that broke out of the app's own menu
            styling. `helperText` carries what "minimum" actually means,
            right where someone is choosing it, instead of a sentence at
            the bottom of the panel nobody connects back to this field. */}
        <TextField
          size="small"
          select
          label="Level"
          value={recommendation}
          onChange={(e) => setRecommendation(e.target.value as 'minimum' | 'recommended')}
          helperText={RECOMMENDATIONS.find((r) => r.value === recommendation)?.hint}
          sx={{ width: 190 }}
          FormHelperTextProps={{ sx: { fontSize: '0.64rem', mx: 0, maxWidth: 190 } }}
        >
          {RECOMMENDATIONS.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="outlined"
          disabled={!tagType.trim() || !tagValue.trim() || add.isPending}
          onClick={() => add.mutate()}
          sx={{ mt: 0.25 }}
        >
          Add tag
        </Button>
      </Stack>

      {/* Why any of this matters, as its own callout rather than a run-on
          sentence with pill badges wedged into it — those badges are
          `inline-flex` boxes wrapping their OWN paragraph text, and MUI
          renders that inner text as a `<p>`; nested inside this paragraph's
          own `<p>`, the browser closed the outer tag at each badge and
          split one sentence into several orphaned lines. Plain monospace
          spans read as inline code here instead, which is what "site" and
          "wh_class" actually are — field names, not status marks. */}
      <Box
        sx={{
          mt: 1.25,
          p: 1,
          borderLeft: `2px solid ${hexToRgba(C.cyan, 0.4)}`,
          bgcolor: hexToRgba(C.cyan, 0.04),
        }}
      >
        <Typography sx={{ color: C.textDim, fontSize: '0.72rem', lineHeight: 1.6, maxWidth: '70ch' }}>
          Pick classes, activities, or both. Class + activity matches both; an activity alone applies across classes.
          Tagging a private fit automatically shares it with your corp. Existing sharing scopes keep their audience.
        </Typography>
      </Box>
    </Box>
  );
};

/** Inline code-style reference to a tag category name, used only inside
 * prose. A `span`, deliberately — anything with its own block-level
 * Typography inside (StatusBadge included) breaks the paragraph it sits in. */
const TagCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT.mono,
      fontSize: '0.9em',
      color: C.cyan,
      border: `1px solid ${hexToRgba(C.cyan, 0.35)}`,
      borderRadius: '3px',
      px: '4px',
      py: '1px',
    }}
  >
    {children}
  </Box>
);

export default FittingTagEditor;
